if (!customElements.get('sticky-atc')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        productTitle: '.m-sticky-addtocart--title',
        mainImage: '.m-sticky-addtocart--image',
        addToCart: '.m-add-to-cart',
        variantIdSelect: '[name="id"]',
        priceTrigger: '.main-product__block-price'
      };
    }

    connectedCallback() {
      this.container = this.closest('.m-sticky-addtocart');
      this.mainProductForm = document.querySelector('.m-product-form--main');
      this.mainProductInfo = document.querySelector('.m-main-product--info');
      this.mainAddToCart = this.mainProductForm?.querySelector('.m-add-to-cart');
      this.priceTrigger = document.querySelector(this.selectors.priceTrigger) || this.mainProductForm;
      this.productId = this.dataset.productId;
      this.variantSelect = this.querySelector(this.selectors.variantIdSelect);
      this.variantData = this.getVariantData();
      this.disableSelectedVariantDefault = this.dataset.disableSelectedVariantDefault === 'true';

      if (!this.mainAddToCart || !this.container) {
        this.setVisible(false);
        return;
      }

      this.querySelector(this.selectors.productTitle)?.addEventListener('click', () => this.scrollToMainForm());
      this.querySelector(this.selectors.mainImage)?.addEventListener('click', () => this.scrollToMainForm());
      this.variantSelect?.addEventListener('change', () => this.handleVariantChange());
      this.querySelector(this.selectors.addToCart)?.addEventListener('click', (event) => this.validateCustomFields(event));

      if (
        !window.location.search.includes('variant=') &&
        this.disableSelectedVariantDefault &&
        this.variantData.length > 1
      ) {
        this.variantSelect.value = '';
        this.updateButton(true, 'Select options');
      }

      this.observePurchaseJourney();
      this.syncWithMainProductForm();
      this.setStickyAddToCartHeight();

      this.handleBreakpointChange = () => {
        this.setStickyAddToCartHeight();
        this.scheduleVisibilityUpdate();
      };
      document.addEventListener('matchMobile', this.handleBreakpointChange);
      document.addEventListener('unmatchMobile', this.handleBreakpointChange);
    }

    getVariantData() {
      const dataNode = this.container?.querySelector('[type="application/json"]');
      if (!dataNode) return [];

      try {
        return JSON.parse(dataNode.textContent);
      } catch (error) {
        return [];
      }
    }

    observePurchaseJourney() {
      this.handleViewportChange = () => this.scheduleVisibilityUpdate();
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange, { passive: true });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          () => this.scheduleVisibilityUpdate(),
          { threshold: [0, 0.01, 1] }
        );
        this.observer.observe(this.priceTrigger);
        if (this.mainAddToCart !== this.priceTrigger) this.observer.observe(this.mainAddToCart);
      }

      this.scheduleVisibilityUpdate();
    }

    scheduleVisibilityUpdate() {
      if (this.visibilityFrame) return;

      this.visibilityFrame = window.requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      if (!this.priceTrigger || !this.mainAddToCart) {
        this.setVisible(false);
        return;
      }

      const headerHeight = window.MinimogSettings?.headerHeight || 66;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const triggerRect = this.priceTrigger.getBoundingClientRect();
      const mainActionRect = this.mainAddToCart.getBoundingClientRect();
      const priceHasPassed = triggerRect.bottom <= headerHeight;
      const mainActionIsVisible =
        mainActionRect.top < viewportHeight && mainActionRect.bottom > headerHeight;

      this.setVisible(priceHasPassed && !mainActionIsVisible);
    }

    setVisible(show) {
      if (this.isVisible === show) return;
      this.isVisible = show;
      this.container?.style.setProperty('--m-translate-y', show ? '0' : '100%');
      document.documentElement.classList.toggle('stick-atc-show', Boolean(show));
    }

    handleVariantChange() {
      const variantId = Number(this.variantSelect?.value);
      const variant = this.variantData.find((item) => item.id === variantId);
      if (!variant) {
        this.updateButton(true, 'Select options');
        return;
      }

      this.syncVariantToMainPicker(variant);
      this.updateButton(!variant.available, variant.available ? '' : window.MinimogStrings?.soldOut);
    }

    syncVariantToMainPicker(variant) {
      const picker = this.mainProductInfo?.querySelector('variant-picker');
      if (!picker || !Array.isArray(variant.options)) return;

      const fields = Array.from(picker.querySelectorAll('[data-picker-field]'));
      variant.options.forEach((value, index) => {
        const field = fields[index];
        if (!field) return;

        if (field.dataset.pickerField === 'radio') {
          const input = Array.from(field.querySelectorAll('input')).find((item) => item.value === value);
          if (input) input.checked = true;
        } else {
          const select = field.querySelector('select');
          if (select) select.value = value;
        }

        field.dataset.selectedValue = value;
        const selectedLabel = field.querySelector('.option-label--selected');
        if (selectedLabel) selectedLabel.textContent = value;
      });

      picker.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateButton(disabled, label) {
      const button = this.querySelector('[name="add"]');
      const buttonText = button?.querySelector('.m-add-to-cart--text');
      if (!button) return;

      button.disabled = disabled;
      button.classList.toggle('disabled', disabled);
      if (buttonText) {
        buttonText.textContent =
          label || window.MinimogStrings?.addToCart || button.dataset.atcText || 'Add to cart';
      }
    }

    validateCustomFields(event) {
      if (!this.mainProductInfo || typeof window.validateForm !== 'function') return;
      if (validateForm(this.mainProductInfo).length === 0) return;

      event.preventDefault();
      event.stopPropagation();
      this.scrollToMainForm(() => this.mainAddToCart.click());
    }

    scrollToMainForm(callback) {
      if (typeof window.__scrollToTop === 'function') {
        window.__scrollToTop(this.mainProductForm, callback);
        return;
      }

      this.mainProductForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (callback) window.setTimeout(callback, 350);
    }

    syncWithMainProductForm() {
      if (!window.MinimogEvents?.subscribe || !this.variantSelect) return;

      window.MinimogEvents.subscribe(`${this.productId}__VARIANT_CHANGE`, (variant) => {
        if (!variant) return;
        this.variantSelect.value = variant.id;
        this.updateButton(!variant.available, variant.available ? '' : window.MinimogStrings?.soldOut);
      });
    }

    setStickyAddToCartHeight() {
      const height = this.container?.offsetHeight || 0;
      document.documentElement.style.setProperty('--f-sticky-atc-bar-height', `${height}px`);
      if (window.MinimogSettings) window.MinimogSettings.stickyAddToCartHeight = height;
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);
      document.removeEventListener('matchMobile', this.handleBreakpointChange);
      document.removeEventListener('unmatchMobile', this.handleBreakpointChange);
      if (this.visibilityFrame) window.cancelAnimationFrame(this.visibilityFrame);
      document.documentElement.classList.remove('stick-atc-show');
    }
  }

  customElements.define('sticky-atc', StickyAddToCart);
}
